import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import * as lightweightCharts from 'lightweight-charts'

import { queryFormatter } from '../loadboardHelpers'
import { baseURL } from '../../../config'

const LoadPriceChart = ({ isDarkMode, load, onCPLoad, onCPHistoryLoad, onDataLoad }) => {
	const chartContainerRef = useRef(null)
	const chartRef = useRef(null)
	const lastFetchedIdRef = useRef(null)
	const [priceStats, setPriceStats] = useState({ min: 0, max: 0, avg: 0 })
	const [loading, setLoading] = useState(true)

	const fetchData = async () => {
		if (lastFetchedIdRef.current === load._id) return
		lastFetchedIdRef.current = load._id
		setLoading(true)
		try {
			const response = await axios.post(`${baseURL}/api/loads/similar`, {
				...queryFormatter(load),
				_id: load._id,
				limitDays: 7,
			})

			onCPLoad?.(response.data.CP || [])
			onCPHistoryLoad?.(response.data.CPHistory || null)
			onDataLoad?.(response.data.data || null)

			let rawData = response.data.loads.map(load => {
				const dateTime = dayjs(load.pickupDate).unix()
				return {
					time: dateTime,
					value: load.price_per_mile,
				}
			})

			if (rawData.length > 0) {
				const prices = rawData
					.map(item => item.value)
					.filter(price => !isNaN(price) && price !== null)
				if (prices.length > 0) {
					const min = Math.min(...prices)
					const max = Math.max(...prices)
					const avg =
						prices.reduce((sum, price) => sum + price, 0) / prices.length
					setPriceStats({ min, max, avg })
				}
			}

			const groupedData = rawData.reduce((acc, item) => {
				if (acc[item.time]) {
					acc[item.time].push(item.value)
				} else {
					acc[item.time] = [item.value]
				}
				return acc
			}, {})

			let data = Object.entries(groupedData).map(([time, values]) => {
				return {
					time: parseInt(time),
					value: values.reduce((a, b) => a + b, 0) / values.length,
				}
			})

			data.sort((a, b) => a.time - b.time)

			if (chartContainerRef.current && data.length > 0) {
				if (chartRef.current) {
					chartRef.current.remove()
					chartRef.current = null
				}

				const { clientWidth, clientHeight } = chartContainerRef.current
				const containerWidth = clientWidth || 600
				const containerHeight = clientHeight || 180

				const chartOptions = {
					width: containerWidth,
					height: containerHeight,
					autoSize: true,
					layout: {
						textColor: isDarkMode ? 'white' : 'black',
						background: {
							type: 'solid',
							color: isDarkMode ? '#1c2128' : 'white',
						},
					},
					timeScale: {
						timeVisible: true,
						secondsVisible: false,
						timezone: 'America/Los_Angeles',
					},
					crosshair: {
						mode: lightweightCharts.CrosshairMode.Normal,
					},
				}

				chartRef.current = lightweightCharts.createChart(
					chartContainerRef.current,
					chartOptions
				)

				const areaSeries = chartRef.current.addAreaSeries({
					lineColor: isDarkMode ? '#2962FF' : '#00b5b5',
					topColor: isDarkMode ? '#2962FF' : '#00b5b5',
					bottomColor: isDarkMode
						? 'rgba(41, 98, 255, 0.28)'
						: 'rgba(0, 181, 181, 0.28)',
				})

				areaSeries.setData(data)
				chartRef.current.timeScale().fitContent()
			}

			setLoading(false)
		} catch (error) {
			setLoading(false)
			if (error.response?.status === 429) {
				console.warn('Rate limit exceeded. Please wait and try again later.')
			} else {
				console.error('An unexpected error occurred:', error)
			}
		}
	}

	useEffect(() => {
		fetchData()
	}, [load])

	useEffect(() => {
		return () => {
			if (chartRef.current) {
				chartRef.current.remove()
				chartRef.current = null
			}
		}
	}, [])

	return (
		<div style={{ width: '100%', minWidth: '300px' }}>
			<div className="text-sm mb-1" style={{ height: '20px' }}>
				{loading
					? 'Loading chart data...'
					: `Min: $${priceStats.min.toFixed(2)}, Max: $${priceStats.max.toFixed(
							2
					  )}, Avg: $${priceStats.avg.toFixed(2)}`}
			</div>
			<div style={{ position: 'relative', height: '180px', width: '100%', minWidth: '300px' }}>
				{loading && (
					<div
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: isDarkMode ? '#1c2128' : '#f9fafb',
							border: `1px solid ${isDarkMode ? '#444' : '#e5e7eb'}`,
							borderRadius: '4px',
							zIndex: 1,
						}}
					>
						<div className="flex items-center space-x-2">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
							<span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
								Loading chart...
							</span>
						</div>
					</div>
				)}
				<div ref={chartContainerRef} style={{ height: '100%', width: '100%' }} />
			</div>
		</div>
	)
}

export default LoadPriceChart
